import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../../../api/api';

const MONTH_KEYS = [
  'month_jan', 'month_feb', 'month_mar', 'month_apr',
  'month_may', 'month_jun', 'month_jul', 'month_aug',
  'month_sep', 'month_oct', 'month_nov', 'month_dec',
];

const YEAR_BAR_COLORS = [
  '#3b82f6', '#f97316', '#8b5cf6', '#ec4899', '#14b8a6',
  '#eab308', '#6366f1', '#f43f5e', '#06b6d4', '#84cc16',
];

const ReportsTab = ({ bookings, apartments, userGroup }) => {
  const { t } = useTranslation();

  const totalBookings = bookings.length;

  const [selectedYear, setSelectedYear] = useState('');
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [selectedYears, setSelectedYears] = useState(null);

  useEffect(() => {
    api.get('/currencies/')
      .then(res => {
        const def = res.data.find(c => c.is_default);
        if (def) setCurrencySymbol(def.symbol);
      })
      .catch(() => {});
  }, []);

  const revenueByYear = useMemo(() => bookings.reduce((acc, booking) => {
    const year = new Date(booking.start_date).getFullYear();
    if (!isNaN(year)) {
      acc[year] = (acc[year] || 0) + (parseFloat(booking.total_price) || 0);
    }
    return acc;
  }, {}), [bookings]);

  const revenueByYearSorted = useMemo(
    () => Object.entries(revenueByYear).sort(([a], [b]) => b - a),
    [revenueByYear]
  );

  const incomeByYearMonth = useMemo(() => bookings.reduce((acc, booking) => {
    const date = new Date(booking.start_date);
    const year = date.getFullYear();
    const month = date.getMonth();
    if (!isNaN(year)) {
      if (!acc[year]) acc[year] = {};
      acc[year][month] = (acc[year][month] || 0) + (parseFloat(booking.total_price) || 0);
    }
    return acc;
  }, {}), [bookings]);

  const availableYears = useMemo(
    () => Object.keys(incomeByYearMonth).map(Number).sort((a, b) => b - a),
    [incomeByYearMonth]
  );

  const effectiveSelectedYears = selectedYears === null ? availableYears : selectedYears;

  const toggleYear = (year) => {
    setSelectedYears(prev => {
      const base = prev === null ? availableYears : prev;
      return base.includes(year) ? base.filter(y => y !== year) : [...base, year];
    });
  };

  const chartData = useMemo(() => MONTH_KEYS.map((key, idx) => {
    const entry = { month: t(key) };
    effectiveSelectedYears.forEach(year => {
      entry[String(year)] = parseFloat(((incomeByYearMonth[year]?.[idx]) || 0).toFixed(2));
    });
    return entry;
  }), [effectiveSelectedYears, incomeByYearMonth, t]);

  const effectiveYear = revenueByYear[selectedYear] !== undefined
    ? selectedYear
    : (revenueByYearSorted.length > 0 ? revenueByYearSorted[0][0] : '');

  const selectedRevenue = revenueByYear[effectiveYear] || 0;

  const upcomingBookings = bookings.filter(booking => {
    const startDate = new Date(booking.start_date);
    const today = new Date();
    return startDate >= today;
  }).length;

  const currencyFormatter = (value) => `${currencySymbol}${Number(value).toFixed(2)}`;

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold mb-6">{t("booking_reports")}</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">{t("total_bookings")}</h3>
          <p className="text-3xl font-bold text-blue-600">{totalBookings}</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">{t("upcoming_bookings")}</h3>
          <p className="text-3xl font-bold text-green-600">{upcomingBookings}</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">{t("total_revenue")}</h3>
          {revenueByYearSorted.length === 0 ? (
            <p className="text-gray-500">—</p>
          ) : (
            <>
              <select
                value={effectiveYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                {revenueByYearSorted.map(([year]) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <p className="text-3xl font-bold text-purple-600">{currencySymbol}{selectedRevenue.toFixed(2)}</p>
            </>
          )}
        </div>
      </div>

      {/* Monthly Income Bar Chart */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">{t("monthly_income")}</h3>

        {availableYears.length === 0 ? (
          <p className="text-gray-500">—</p>
        ) : (
          <>
            {/* Year checkboxes */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="text-sm font-medium text-gray-600">{t("select_years")}:</span>
              {availableYears.map((year, idx) => (
                <label key={year} className="flex items-center gap-1 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={effectiveSelectedYears.includes(year)}
                    onChange={() => toggleYear(year)}
                    className="w-4 h-4"
                    style={{ accentColor: YEAR_BAR_COLORS[idx % YEAR_BAR_COLORS.length] }}
                  />
                  <span
                    className="font-medium"
                    style={{ color: YEAR_BAR_COLORS[idx % YEAR_BAR_COLORS.length] }}
                  >
                    {year}
                  </span>
                </label>
              ))}
            </div>

            {/* Bar chart */}
            <div style={{ height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={currencyFormatter} tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={currencyFormatter} />
                  <Legend />
                  {effectiveSelectedYears
                    .slice()
                    .sort()
                    .map((year) => {
                      const idx = availableYears.indexOf(year);
                      return (
                        <Bar
                          key={year}
                          dataKey={String(year)}
                          name={String(year)}
                          fill={YEAR_BAR_COLORS[idx % YEAR_BAR_COLORS.length]}
                        />
                      );
                    })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsTab;